import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, Injector, Input, NgZone, OnChanges, OnInit, signal, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateRange, DefaultMatCalendarRangeStrategy, MAT_DATE_RANGE_SELECTION_STRATEGY } from '@angular/material/datepicker';
import { MatSelectChange } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { AbstractComponent, CacheKeyEnum, CardDiscardReasonEnum, CardPriorityEnum, CardSavedFilter, CardSavedFilterService, CardService, Customer, CustomerService, DateUtil, DISCARD_REASON_LABEL, FilterDefinition, FilterOption, Funnel, isPlatformLossReason, LossReason, LossReasonService, PRIORITY_LABEL, Product, ProductService, User, UserService } from 'lib-trend-core';
import { debounceTime, distinctUntilChanged, finalize, mergeMap, Subject, takeUntil, tap } from 'rxjs';
import { setQueryParams } from '../state/actions';
import { AppCRMState } from '../state/app.state';

/** One filter as the bar shows it: a chip, applied or just added and still without a value. */
export interface FilterChipView {
  key: string;
  def: FilterDefinition;
  label: string;
  /** "é" for a list, "≥" / "≤" for a score bound; empty when the label ends with a colon. */
  verb: string;
  value: string;
  active: boolean;
  title: string;
  /** The width the chip renders at, estimated from its text (see `measureChip`). */
  width: number;
}

/**
 * Mirrors `.filter-chip { max-width }` in the stylesheet — change both together. A chip
 * longer than this truncates its value, so the estimate caps at the same figure.
 */
const CHIP_MAX_WIDTH_PX = 240;
/** Everything around a chip's text: paddings, gaps, the chevron, the remove button, the border. */
const CHIP_CHROME_PX = 67;
/** The "e" connector between two chips, with its gaps. */
const CONNECTOR_WIDTH_PX = 26;
/** Room kept for the "+N" button whenever at least one chip does not fit. */
const MORE_BUTTON_WIDTH_PX = 64;
/** The line shows at most this many chips, however wide it is; the rest go behind "+N". */
const MAX_VISIBLE_CHIPS = 4;
/** Fallback for the text measurement until the chip area reports its computed font. */
const DEFAULT_CHIP_FONT = '13px Nunito, sans-serif';
/** Option lists longer than this get a search field. */
const SEARCHABLE_LIST_MIN = 6;
/** Whether the filter section is shown: a screen preference per user, the same for every funnel. */
const SECTION_OPEN_KEY_PREFIX = 'kanban:filtersOpen:';

@Component({
    selector: 'app-filter-panel',
    templateUrl: './filter-panel.component.html',
    styleUrl: './filter-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    /**
     * The date editor renders the platform calendar INLINE (`mat-calendar`) instead of
     * opening the range picker's overlay: a popup anchored from inside a popover came out
     * at the page corner, and the range input's self-sizing start field collapsed there.
     * The strategy gives the inline calendar the same hover preview the range picker has.
     */
    providers: [{ provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: DefaultMatCalendarRangeStrategy }],
})
export class FilterPanelComponent extends AbstractComponent implements OnInit, OnChanges, AfterViewInit {
  /**
   * The search field and the out-of-profile button, rendered by the board's header row
   * (`kanban.component.html`) through an `ngTemplateOutlet`: they sit in the toolbar while
   * the bar below is a row of its own, and both read the one filter state kept here.
   */
  @ViewChild('toolbar', { static: true }) toolbar!: TemplateRef<unknown>;
  @ViewChild('chipsArea', { static: true }) private chipsArea!: ElementRef<HTMLElement>;

  @Input() definitions: Array<FilterDefinition> = [];
  @Input() selectedFunnel: Funnel | null = null;
  /** Deals the board shows under the current filters; null until the stage totals arrive. */
  @Input() shownCount: number | null = null;
  /** Deals the funnel holds without any filter; null until the base totals arrive. */
  @Input() totalCount: number | null = null;

  /** Every chip, newest first — the single list the bar, the "+N" popover and the badge read. */
  public chipViews = signal<Array<FilterChipView>>([]);
  /** Width of the chip area, measured by a ResizeObserver; 0 until the first measure. */
  public availableWidth = signal<number>(0);
  private readonly layout = computed(() => this.fitChips(this.chipViews(), this.availableWidth()));
  public visibleChips = computed(() => this.layout().visible);
  public overflowChips = computed(() => this.layout().overflow);
  public activeCount = computed(() => this.chipViews().filter(chip => chip.active).length);

  /** The chip whose editor is open, the "add filter" menu and the "+N" popover. */
  public openKey = signal<string | null>(null);
  public addOpen = signal<boolean>(false);
  public moreOpen = signal<boolean>(false);
  public anyMenuOpen = computed(() => !!this.openKey() || this.addOpen() || this.moreOpen());
  /** The date chip whose calendar is unfolded — it opens along with the chip; the toggle folds it. */
  public calendarKey = signal<string | null>(null);

  /** The definitions split into the sections the "add filter" menu renders (see `buildFilterGroups`). */
  public filterGroups = signal<Array<{ label: string; items: Array<FilterDefinition> }>>([]);
  /** The sections minus the filters already on the bar and the hidden ones. */
  public addMenuGroups = computed(() => {
    const onBar = new Set(this.chipViews().map(chip => chip.key));
    return this.filterGroups()
      .map(group => ({ label: group.label, items: group.items.filter(def => !def.hidden && !onBar.has(def.key)) }))
      .filter(group => group.items.length > 0);
  });
  /** The options of the open chip's list, already filtered by `listSearch`. */
  public menuOptions = signal<Array<FilterOption>>([]);

  public listSearch = new FormControl('');
  public searchControl = new FormControl('');

  public outOfIcpCount = signal<number | null>(null);
  public originOptions = signal<Array<{ value: string; label: string }>>([]);

  /** The user's saved filters of the selected funnel, oldest first (RF-02). */
  public savedFilters = signal<Array<CardSavedFilter>>([]);
  /** The API's limit per user and funnel, read from the list's `meta`; null until it arrives. */
  public savedLimit = signal<number | null>(null);
  /** The saved filters whose values are the applied ones (RF-15). */
  public activeSavedIds = signal<ReadonlySet<string>>(new Set());
  /** "Salvar filtro" is offered while something is applied that no saved filter holds (RF-04). */
  public canOfferSave = computed(() => this.activeCount() > 0 && this.activeSavedIds().size === 0);
  public atSavedLimit = computed(() => {
    const limit = this.savedLimit();
    return limit !== null && this.savedFilters().length >= limit;
  });
  public saving = signal<boolean>(false);
  public saveOpen = signal<boolean>(false);
  public saveNameControl = new FormControl('');
  public confirmDeleteId = signal<string | null>(null);
  /** The chip bar and the saved filters, shown or hidden by the toolbar's toggle; hidden until the user opens it. */
  public sectionOpen = signal<boolean>(false);
  /** The toggle's tooltip and accessible name: what a click does. */
  public sectionToggleLabel = computed(() => this.sectionOpen() ? 'kanban.filterPanel.closeFilters' : 'kanban.filterPanel.openFilters');

  priorities = Object.values(CardPriorityEnum).map(v => ({ value: v, label: PRIORITY_LABEL[v] }));

  public listUsers: Array<User> = [];
  public listProducts: Array<Product> = [];
  public listCustomers: Array<Customer> = [];
  public listLossReasons: Array<LossReason> = [];
  public canFilterUnassigned = false;

  /** Chip keys, newest first. Persisted with the filters so the bar reopens as it was left. */
  private chipOrder: Array<string> = [];
  /**
   * The value being edited in the open chip's editor (a list or a score bound). Nothing
   * reaches the board until that editor's "Aplicar"; closing it discards this. A date
   * filter drafts in its form group instead, committed by the calendar's OK.
   */
  private draft: unknown = undefined;
  private appliedFunnelId: string | null = null;
  private readonly dispatchRequest$ = new Subject<void>();
  private readonly ngZone: NgZone;
  private resizeObserver?: ResizeObserver;
  private measureContext: CanvasRenderingContext2D | null = null;
  private chipFonts: { regular: string; bold: string } | null = null;

  constructor(
    injector: Injector,
    private store: Store<AppCRMState>,
    private userService: UserService,
    private productService: ProductService,
    private customerService: CustomerService,
    private lossReasonService: LossReasonService,
    private cardService: CardService,
    private cardSavedFilterService: CardSavedFilterService,
  ) {
    super(injector);
    this.ngZone = injector.get(NgZone);
    this.cacheKey = CacheKeyEnum.CRM_FILTERS;
    this.setupForm();
  }

  ngOnInit(): void {
    this.resetToDefaults();
    this.buildFilterGroups();
    this.refreshChips();
    this.sectionOpen.set(this.readSectionOpen());

    this.userService.getList()
      .pipe(
        takeUntil(this.destroy$),
        tap((users: Array<User>) => {
          /**
           * An owner-restricted salesperson only ever gets their own deals back, so the
           * owner filter is narrowed to themselves — the same thing the `/tasks` screen
           * does when it is opened for one user (`listUser = [userLogged]`). Offering
           * the whole company here would offer filters whose only possible answer is an
           * empty board, with nothing on screen to explain why.
           *
           * Presentation only: the restriction itself is applied by the API on every
           * card read (ADR-003).
           */
          const restricted = !this.canViewAllDeals();
          const idCurrentUser = this.getIDCurrentUser();
          this.listUsers = restricted ? users.filter((user: User) => user._id === idCurrentUser) : users;
          this.canFilterUnassigned = this.isAdmin() || this.isSupervisor();
        }),
        mergeMap(() => this.productService.getList()),
        tap((products) => this.listProducts = products),
        mergeMap(() => this.customerService.getList()),
        tap((customers) => this.listCustomers = customers),
        mergeMap(() => this.lossReasonService.getList()),
      ).subscribe((lossReasons) => {
        this.listLossReasons = lossReasons;
        // The chips name their values from these lists, so they are rebuilt once the lists land.
        this.refreshChips();
        this.rebuildMenuOptions();
      });

    /**
     * A filter reaches the board when its editor's "Aplicar" commits it, or at once from a
     * direct action — a chip's remove button, "Limpar filtros", the out-of-profile toggle.
     * The short debounce folds a burst of those into a single board reload.
     */
    this.dispatchRequest$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.dispatchQueryParams());

    this.listSearch.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.rebuildMenuOptions());

    this.setupGlobalSearch();
    this.refreshChipsWhenFontsLoad();
  }

  ngAfterViewInit(): void {
    this.observeChipArea();
  }

  override ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    super.ngOnDestroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    /**
     * The board appends the radar definitions in its own `ngOnInit`, so the panel
     * receives them as a second emission and has to regroup — and rebuild the chips,
     * which are derived from `definitions`: a restored radar filter applied to the board
     * before its definition arrived has no chip until this runs.
     */
    if (changes['definitions']) {
      this.ensureDateGroups();
      this.buildFilterGroups();
      this.refreshChips();
    }

    const idFunnel = (changes['selectedFunnel']?.currentValue as Funnel | null)?._id;
    if (!idFunnel || idFunnel === this.appliedFunnelId) return;

    this.appliedFunnelId = idFunnel;
    this.initForFunnel(idFunnel);
  }

  private initForFunnel(idFunnel: string): void {
    this.listCache.setScope(`${this.getIDCurrentUser()}.${idFunnel}`);

    if (!this.restoreFilters()) {
      this.resetToDefaults();
      this.chipOrder = [];
    }

    this.setValueSilently('funnel', idFunnel);
    this.refreshChips();
    this.dispatchQueryParams();
    this.loadOriginOptions();
    this.loadSavedFilters(idFunnel);
  }

  private loadOriginOptions(): void {
    if (!this.selectedFunnel?._id) return;

    this.cardService
      .getOriginOptions({ funnel: this.selectedFunnel._id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (options) => {
          this.originOptions.set(options ?? []);
          this.refreshChips();
          this.rebuildMenuOptions();
        },
        error: () => this.originOptions.set([]),
      });
  }

  /**
   * One nested group per date filter — `<key>Range` holding `<key>Start` / `<key>End` —
   * built from the definitions rather than declared one by one: the three date filters
   * (creation, win, loss) share one editor, the platform's range picker.
   */
  setupForm(): void {
    this.formGroup = this.formBuilder.group({});
    this.ensureDateGroups();
  }

  private ensureDateGroups(): void {
    (this.definitions ?? [])
      .filter(def => def.type === 'daterange')
      .forEach(def => {
        if (this.formGroup.get(`${def.key}Range`)) return;
        this.formGroup.addControl(`${def.key}Range`, this.formBuilder.group({
          [`${def.key}Start`]: [null],
          [`${def.key}End`]: [null],
        }));
        this.restoreDateGroup(def.key);
      });
  }

  /**
   * The board's period, set from outside the panel — the pipeline overview's presets (RF-08).
   * Same path as the picker, so it is persisted and dispatched exactly once.
   */
  public setDateRange(from: Date, to: Date): void {
    this.formGroup.get('dateRange')?.patchValue({ dateStart: from, dateEnd: to });
    this.applyDateRange('date');
  }

  private queueDispatch(): void {
    this.dispatchRequest$.next();
  }

  private dispatchQueryParams(): void {
    this.updateSearchParams();
    this.persistFilters();
    this.store.dispatch(setQueryParams({
      queryParams: { search: this.searchString || '', params: this.searchParams }
    }));
    this.refreshOutOfIcpCount();
  }

  private refreshOutOfIcpCount(): void {
    if (!this.selectedFunnel?._id) return;

    this.cardService
      .getOutOfIcpCount({
        ...(this.searchParams as Record<string, string>),
        funnel: this.selectedFunnel._id,
        ...(this.searchString ? { search: this.searchString } : {}),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => this.outOfIcpCount.set(result?.count ?? 0),
        error: () => this.outOfIcpCount.set(null),
      });
  }

  /** The chip order travels with the filters, so the bar reopens exactly as it was left. */
  private persistFilters(): void {
    this.saveState({
      search: this.searchString || '',
      params: this.searchParams,
      extraData: { chips: this.activeChipOrder() },
    });
  }

  private restoreFilters(): boolean {
    const cached = this.restoreState();

    if (!cached?.params || Object.keys(cached.params).length === 0) return false;

    this.applyFilterSet(cached.params, (cached.extraData?.['chips'] as Array<string> | undefined) ?? null);
    this.searchString = cached.search ?? '';
    this.searchControl.setValue(this.searchString, { emitEvent: false });

    return true;
  }

  /**
   * Puts a filter set on the bar: the remembered one on restore, or a saved one when applied
   * (RF-12). A set with no chip order (`null`) was written by the panel before the bar.
   */
  private applyFilterSet(params: Record<string, unknown>, chips: Array<string> | null): void {
    this.searchParams = this.dropRetiredFilters({ ...params }, chips !== null);
    this.chipOrder = this.orderFromCache(chips ?? []);

    (this.definitions ?? [])
      .filter(def => def.type === 'daterange')
      .forEach(def => this.restoreDateGroup(def.key));
  }

  /**
   * Params of retired filters, dropped on restore.
   *
   * Everything downstream keeps a param it does not recognise: `updateSearchParams`
   * forwards any non-empty value, while the chips are built from the DEFINITIONS. So a
   * filter whose definition is gone keeps narrowing the board with no chip and no
   * control to clear it — until someone presses "clear filters".
   *
   * `includeBlacklisted` was retired on 2026-08-21. The period is the second case: the
   * board used to open on a default 30-day window that the old panel wrote into the
   * cache; since the bar (2026-10-27) the period is "Data de criação", a filter like any
   * other with NO default, so a cache written by the old panel — recognisable by the
   * absence of the chip order — hands that window back only if the user had chosen it,
   * which the old cache cannot tell. It is dropped; the user picks a period when wanted.
   *
   * A deny-list rather than a check against `definitions`: the board appends the radar
   * definitions in its own `ngOnInit` (see `ngOnChanges`), so this can run while
   * `definitions` is still the base set — validating against it would strip every radar
   * filter the user had applied.
   */
  private dropRetiredFilters(params: Record<string, unknown>, writtenByTheBar: boolean): Record<string, unknown> {
    delete params['includeBlacklisted'];
    if (!writtenByTheBar) {
      delete params['dateStart'];
      delete params['dateEnd'];
    }
    return params;
  }

  /** The persisted order, kept to the keys still active, plus any active key it misses. */
  private orderFromCache(order: Array<string>): Array<string> {
    const active = this.activeKeys();
    return [...order.filter(key => active.includes(key)), ...active.filter(key => !order.includes(key))];
  }

  private restoreDateGroup(key: string): void {
    const start = this.toPickerDate(this.searchParams?.[`${key}Start`]);
    const end = this.toPickerDate(this.searchParams?.[`${key}End`]);

    this.formGroup.get(`${key}Range`)?.patchValue(
      { [`${key}Start`]: start, [`${key}End`]: end },
      { emitEvent: false }
    );
  }

  /** A persisted filter date: a calendar day (`yyyy-MM-dd`), or the epoch milliseconds an earlier build stored. */
  private toPickerDate(value: unknown): Date | null {
    const day = DateUtil.fromCalendarDay(value);
    if (day) return day;

    if (value === null || value === undefined || value === '') return null;
    const instant = new Date(Number(value));
    return isNaN(instant.getTime()) ? null : instant;
  }

  // ---------------------------------------------------------------------------------
  // Menus
  // ---------------------------------------------------------------------------------

  /**
   * Opens a chip's editor on a draft of its applied value. A chip inside the "+N" popover
   * keeps the popover open, so the other hidden chips stay reachable while one is being
   * edited. Whatever editor was open loses its draft.
   */
  public toggleChip(key: string, inMore: boolean): void {
    const opening = this.openKey() !== key;
    this.discardDraft();
    this.openKey.set(opening ? key : null);
    this.addOpen.set(false);
    this.moreOpen.set(inMore);
    this.listSearch.setValue('', { emitEvent: false });

    if (!opening) {
      this.calendarKey.set(null);
      return;
    }

    const def = this.definitionOf(key);
    if (def) this.startDraft(def);
    this.rebuildMenuOptions();
    // The date editor is the calendar itself: it unfolds along with the chip.
    this.calendarKey.set(def?.type === 'daterange' ? key : null);
  }

  public toggleCalendar(key: string): void {
    this.calendarKey.set(this.calendarKey() === key ? null : key);
  }

  public toggleAdd(): void {
    this.discardDraft();
    this.addOpen.update(open => !open);
    this.openKey.set(null);
    this.moreOpen.set(false);
  }

  public toggleMore(): void {
    this.discardDraft();
    this.moreOpen.update(open => !open);
    this.openKey.set(null);
    this.addOpen.set(false);
  }

  /** Closes every popover. An editor closed this way keeps nothing: its draft is discarded. */
  @HostListener('document:keydown.escape')
  public closeMenus(): void {
    this.discardDraft();
    this.openKey.set(null);
    this.addOpen.set(false);
    this.moreOpen.set(false);
    this.calendarKey.set(null);
  }

  /** Puts a new chip at the front of the bar and opens its editor right away. */
  public addFilter(def: FilterDefinition): void {
    this.chipOrder = [def.key, ...this.chipOrder.filter(key => key !== def.key)];
    this.refreshChips();
    this.addOpen.set(false);
    this.moreOpen.set(false);
    this.toggleChip(def.key, false);
  }

  // ---------------------------------------------------------------------------------
  // Drafts — what an editor holds until its "Aplicar"
  // ---------------------------------------------------------------------------------

  /** The editor starts from the applied value; a date filter's draft is its form group, reset to the applied range. */
  private startDraft(def: FilterDefinition): void {
    if (def.type === 'daterange') {
      this.restoreDateGroup(def.key);
      return;
    }
    const applied = this.getValue(def.key);
    this.draft = Array.isArray(applied) ? [...applied] : (applied ?? (def.type === 'multiselect' ? [] : null));
  }

  /**
   * Drops the open editor's draft. A chip added for a filter that was never applied has
   * nothing left to show, so it leaves the bar: what the bar shows is what is applied.
   */
  private discardDraft(): void {
    const key = this.openKey();
    this.draft = undefined;
    if (!key) return;

    if (this.definitionOf(key)?.type === 'daterange') this.restoreDateGroup(key);
    if (!this.isKeyActive(key)) this.removeChipSilently(key);
  }

  /** Commits the open editor: an empty draft removes the filter, a value applies it. Either way the editor closes. */
  public applyDraft(def: FilterDefinition): void {
    const value = this.draft;
    this.draft = undefined;
    this.openKey.set(null);

    if (this.isActiveValue(value)) {
      this.setValue(def.key, value);
    } else {
      this.clearFilter(def.key);
    }

    this.closeMenus();
  }

  /** "Limpar" inside an editor empties the draft; the filter itself only goes with "Aplicar". */
  public clearDraft(def: FilterDefinition): void {
    this.draft = def.type === 'multiselect' ? [] : null;
  }

  /** The score bound as typed so far, for the editor's input. */
  public draftNumber(): string | number {
    return (this.draft as number | null | undefined) ?? '';
  }

  private removeChipSilently(key: string): void {
    if (!this.chipOrder.includes(key)) return;
    this.chipOrder = this.chipOrder.filter(chipKey => chipKey !== key);
    this.refreshChips();
  }

  // ---------------------------------------------------------------------------------
  // Values
  // ---------------------------------------------------------------------------------

  public getValue(key: string): unknown {
    return this.searchParams?.[key];
  }

  /** Writes one filter value, keeps its chip on the bar and reloads the board. */
  public setValue(key: string, value: unknown): void {
    this.setValueSilently(key, value);
    if (this.isActiveValue(value)) this.ensureChip(key);
    this.refreshChips();
    this.queueDispatch();
  }

  private setValueSilently(key: string, value: unknown): void {
    this.searchParams = { ...this.searchParams, [key]: value };
    this.updateSearchParams();
  }

  private ensureChip(key: string): void {
    if (!this.chipOrder.includes(key)) this.chipOrder = [key, ...this.chipOrder];
  }

  /** An option's check mark reads the draft while its editor is open, the applied value otherwise. */
  public isSelected(def: FilterDefinition, value: string | number): boolean {
    const current = this.openKey() === def.key ? this.draft : this.getValue(def.key);
    if (def.key === 'user' && value === 'unassigned') return current === 'unassigned';
    if (def.type === 'multiselect') return Array.isArray(current) && current.includes(value);
    return current === value;
  }

  /**
   * One click on an option, in the draft. A multi-value list toggles the option; a
   * single-value one sets it, or clears it when it was the one selected. The owner's
   * *unassigned* value (RF-16, AC-13) is a sentinel the API reads as "deals that belong
   * to nobody", not an id: it never combines with a picked owner, so it replaces the
   * owner list and is replaced by it.
   */
  public pickOption(def: FilterDefinition, value: string | number): void {
    if (def.key === 'user' && value === 'unassigned') {
      this.draft = this.draft === 'unassigned' ? [] : 'unassigned';
      return;
    }

    if (def.type === 'multiselect') {
      const current = Array.isArray(this.draft) ? (this.draft as Array<string | number>) : [];
      this.draft = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return;
    }

    this.draft = this.draft === value ? (def.defaultValue ?? null) : value;
  }

  /** The score bounds are 0–100 by definition, so the input is bounded rather than free. Drafted until "Aplicar". */
  public onNumberChange(value: string): void {
    const parsed = value === '' || value === null ? null : Math.max(0, Math.min(100, Number(value)));
    this.draft = parsed === null || Number.isNaN(parsed) ? null : parsed;
  }

  /**
   * Drops ONE filter, from its chip's remove button (RF-16): the value is reset to the
   * definition's own `defaultValue` — `multiselect` defaults to `[]` and `select` to
   * `null`, and writing the wrong empty value leaves the filter looking active — and the
   * chip leaves the bar.
   */
  public clearFilter(key: string): void {
    const def = this.definitionOf(key);
    if (!def) return;

    if (def.type === 'daterange') {
      this.searchParams = { ...this.searchParams, [`${key}Start`]: null, [`${key}End`]: null };
      this.formGroup.get(`${key}Range`)?.reset({ [`${key}Start`]: null, [`${key}End`]: null });
      this.updateSearchParams();
    } else {
      this.setValueSilently(key, def.defaultValue ?? null);
    }

    this.chipOrder = this.chipOrder.filter(chipKey => chipKey !== key);
    if (this.openKey() === key) this.openKey.set(null);
    this.refreshChips();
    this.queueDispatch();
  }

  public isOutOfIcpOnly(): boolean {
    return String(this.getValue('onlyOutOfIcp')) === 'true';
  }

  /** The out-of-profile count button is also the toggle of its filter, which then shows as a chip. */
  public toggleOutOfIcpOnly(): void {
    if (this.isOutOfIcpOnly()) {
      this.clearFilter('onlyOutOfIcp');
      return;
    }
    this.setValue('onlyOutOfIcp', 'true');
  }

  public isUnassignedOwner(): boolean {
    return this.getValue('user') === 'unassigned';
  }

  public clearFilters(): void {
    this.resetToDefaults();
    (this.definitions ?? [])
      .filter(def => def.type === 'daterange')
      .forEach(def => this.formGroup.get(`${def.key}Range`)?.reset({ [`${def.key}Start`]: null, [`${def.key}End`]: null }));

    if (this.selectedFunnel?._id) {
      this.setValueSilently('funnel', this.selectedFunnel._id);
    }

    this.chipOrder = [];
    // With nothing applied there is nothing to save (RF-04): the name field closes with the last filter.
    this.saveOpen.set(false);
    this.confirmDeleteId.set(null);
    this.closeMenus();
    this.refreshChips();
    this.queueDispatch();
  }

  // ---------------------------------------------------------------------------------
  // Date filters — the platform calendar, inline, presets included
  // ---------------------------------------------------------------------------------

  /** The two ends of a date filter as the calendar and the fields show them, drafted in its form group. */
  public dateDraft(key: string): DateRange<Date> {
    const group = this.formGroup.get(`${key}Range`);
    return new DateRange<Date>(group?.get(`${key}Start`)?.value ?? null, group?.get(`${key}End`)?.value ?? null);
  }

  /** OK applies a complete range, or an emptied one — which removes the filter. A half-picked range waits. */
  public canApplyDate(key: string): boolean {
    const draft = this.dateDraft(key);
    return (!!draft.start && !!draft.end) || (!draft.start && !draft.end);
  }

  /**
   * One click on the calendar: the first picks the start, the second the end; a click
   * before the start moves the start; a click once both are set starts over — the way the
   * platform's range picker reads clicks.
   */
  public pickCalendarDate(key: string, date: Date | null): void {
    if (!date) return;

    const draft = this.dateDraft(key);
    let start: Date | null = draft.start;
    let end: Date | null = draft.end;

    if (!start || (start && end)) {
      start = date;
      end = null;
    } else if (date < start) {
      start = date;
    } else {
      end = date;
    }

    this.formGroup.get(`${key}Range`)?.patchValue({ [`${key}Start`]: start, [`${key}End`]: end });
  }

  /** A preset chosen below the calendar fills the two dates; OK applies them, like any picked range. */
  public onDatePreset(key: string, event: MatSelectChange): void {
    const period = parseInt(event.value, 10);
    if (isNaN(period)) return;

    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - period);

    this.formGroup.get(`${key}Range`)?.patchValue({ [`${key}Start`]: start, [`${key}End`]: end });
    event.source.value = null;
  }

  /** "Limpar" below the calendar empties the drafted range; OK then removes the filter. */
  public clearDateDraft(key: string): void {
    this.formGroup.get(`${key}Range`)?.reset({ [`${key}Start`]: null, [`${key}End`]: null });
  }

  /**
   * A date range travels as two CALENDAR DAYS in `<key>Start` / `<key>End` (`yyyy-MM-dd`,
   * SPEC-2026-10 RF-05 · ADR-001 — the attendance dashboard's contract), never as
   * instants: the server resolves them in the company's timezone, so the figures do not
   * depend on where the browser is. The board's period (`dateStart` / `dateEnd`) reads
   * each deal by the date its outcome names — won by its winning moment, lost by its
   * losing moment, open by its creation (RF-03).
   */
  public applyDateRange(key: string): void {
    const group = this.formGroup.get(`${key}Range`);
    const start = group?.get(`${key}Start`)?.value;
    const end = group?.get(`${key}End`)?.value;

    if (start && end) {
      this.searchParams = {
        ...this.searchParams,
        [`${key}Start`]: DateUtil.toCalendarDay(new Date(start)),
        [`${key}End`]: DateUtil.toCalendarDay(new Date(end)),
      };
      this.ensureChip(key);
    } else {
      this.searchParams = { ...this.searchParams, [`${key}Start`]: null, [`${key}End`]: null };
    }

    this.updateSearchParams();
    // Applied before the close, so the discard the close runs finds the draft equal to the filter.
    this.closeMenus();
    this.refreshChips();
    this.queueDispatch();
  }

  // ---------------------------------------------------------------------------------
  // Option lists
  // ---------------------------------------------------------------------------------

  public getFilterOptions(def: FilterDefinition): Array<FilterOption> {
    if (def.options?.length) {
      return def.options;
    }

    switch (def.key) {
      case 'priority':
        return this.priorities;
      default:
        return [];
    }
  }

  /**
   * Filters whose options are DATA, not a fixed list: their values are named from a
   * loaded list rather than from `def.options` — which the board declares empty for
   * exactly these keys — and the names must never go through `translate`.
   */
  public hasOwnOptionBlock(def: FilterDefinition): boolean {
    return ['user', 'product', 'customer', 'lossReason', 'origin'].includes(def.key);
  }

  public hasSearch(def: FilterDefinition): boolean {
    return this.allOptionsFor(def).length > SEARCHABLE_LIST_MIN;
  }

  /** Every option of a list, labels resolved: translated for a fixed list, verbatim for data. */
  private allOptionsFor(def: FilterDefinition): Array<FilterOption> {
    switch (def.key) {
      case 'user':
        return [
          ...(this.canFilterUnassigned ? [{ value: 'unassigned', label: this.t('kanban.filters.unassigned') }] : []),
          ...this.listUsers.map(user => ({ value: user._id, label: user.name })),
        ];
      case 'product': return this.listProducts.map(product => ({ value: product._id, label: product.name }));
      case 'customer': return this.listCustomers.map(customer => ({ value: customer._id, label: customer.name }));
      case 'lossReason': return this.listLossReasons.map(reason => ({ value: reason._id, label: this.lossReasonLabel(reason) }));
      case 'origin': return this.originOptions();
      default: return this.getFilterOptions(def).map(option => ({ value: option.value, label: this.t(option.label) }));
    }
  }

  /** Rebuilt when a chip opens and on every keystroke of its search, never per change-detection pass. */
  private rebuildMenuOptions(): void {
    const def = this.openKey() ? this.definitionOf(this.openKey() as string) : null;
    if (!def || def.type === 'number' || def.type === 'daterange') {
      this.menuOptions.set([]);
      return;
    }

    const term = (this.listSearch.value || '').toLowerCase();
    this.menuOptions.set(this.allOptionsFor(def).filter(option => !term || option.label?.toLowerCase().includes(term)));
  }

  /**
   * E3 ADR-001: the list offers the platform discard reasons beside the company's rows. A row's
   * name is data; a platform reason's label is a catalog key, translated here.
   */
  lossReasonLabel(reason: LossReason): string {
    return isPlatformLossReason(reason)
      ? this.t(DISCARD_REASON_LABEL[reason._id as CardDiscardReasonEnum] ?? reason.name)
      : reason.name;
  }

  // ---------------------------------------------------------------------------------
  // Chips
  // ---------------------------------------------------------------------------------

  /**
   * The chips, in their order (RF-16) — the single writer of `chipViews`, which is also
   * where the active count and the bar's "+N" split come from: one list, counted where it
   * is built, cannot disagree with itself.
   *
   * Rebuilt from `definitions` on every call rather than cached per definition: the
   * board appends the radar filters as a second `definitions` emission, so a list built
   * once would permanently miss them (`ngOnChanges`). An active key the order does not
   * know yet — restored before its definition arrived — is appended here, at the end.
   */
  private refreshChips(): void {
    const defs = this.definitions ?? [];
    this.activeKeys().forEach(key => {
      if (!this.chipOrder.includes(key)) this.chipOrder = [...this.chipOrder, key];
    });

    const views = this.chipOrder
      .map(key => defs.find(def => def.key === key))
      .filter((def): def is FilterDefinition => !!def)
      .map(def => this.chipViewOf(def));

    this.chipViews.set(views);
    this.refreshActiveSaved();
  }

  private chipViewOf(def: FilterDefinition): FilterChipView {
    const label = this.t(def.label);
    const active = this.isKeyActive(def.key);
    const verb = active ? this.verbFor(def) : '';
    const value = active ? this.buildDisplay(def, this.getValue(def.key)) : this.t('kanban.filterPanel.selectValue');
    const shownLabel = verb ? label : `${label}:`;

    return {
      key: def.key,
      def,
      label: shownLabel,
      verb,
      value,
      active,
      title: `${shownLabel} ${verb} ${value}`.replace(/\s+/g, ' ').trim(),
      width: this.measureChip(shownLabel, verb, value),
    };
  }

  /** "é" before a list of options, the bound's own sign before a score; a colon on the label otherwise. */
  private verbFor(def: FilterDefinition): string {
    if (def.type === 'multiselect') return this.t('kanban.filterPanel.is');
    if (def.key === 'scoreMin') return '≥';
    if (def.key === 'scoreMax') return '≤';
    return '';
  }

  private definitionOf(key: string): FilterDefinition | undefined {
    return (this.definitions ?? []).find(def => def.key === key);
  }

  /** The keys whose value narrows the board right now, in definition order. */
  private activeKeys(): Array<string> {
    return (this.definitions ?? []).filter(def => this.isKeyActive(def.key)).map(def => def.key);
  }

  private isKeyActive(key: string): boolean {
    const def = this.definitionOf(key);
    if (def?.type === 'daterange') return this.isActiveValue(this.searchParams?.[`${key}Start`]);
    return this.isActiveValue(this.searchParams?.[key]);
  }

  /**
   * The human label of ONE filter value, or null when there is no way to name it.
   *
   * Three kinds of value reach this, and printing them the same way is what put raw
   * ObjectIds and untranslated i18n keys on the old panel:
   *
   * - **Entity ids** (owner, product, customer, loss reason, origin). The board declares
   *   these filters with `options: []` and the lists are loaded here, so they are resolved
   *   from the loaded list, and the name is DATA: it must never reach `translate`.
   * - **Fixed-value options** (priority, urgency, qualification, stagnation). Their
   *   `label` is an i18n KEY by convention (`PRIORITY_LABEL`, `URGENCY_LABEL`,
   *   `QUALIFICATION_LABEL` all store keys, never literals), so it has to be translated.
   * - The owner filter's `unassigned` **sentinel**, which is not an id at all.
   *
   * Null rather than the raw value when nothing resolves: the lists arrive over four
   * chained requests, so a chip rendered before they land, or one naming an option
   * since deleted, has genuinely nothing to say.
   */
  private labelFor(def: FilterDefinition, value: unknown): string | null {
    if (!this.isActiveValue(value)) return null;

    // A bound of the score range is its own label.
    if (def.type === 'number') return `${value}`;

    if (def.key === 'user' && value === 'unassigned') return this.t('kanban.filters.unassigned');

    if (this.hasOwnOptionBlock(def)) return this.entityLabelFor(def.key, value);

    const optionLabel = this.getFilterOptions(def).find(option => option.value === value)?.label;
    return optionLabel ? this.t(optionLabel) : null;
  }

  /** Entity-backed filters name their values from the loaded lists — only the platform discard reasons are translated. */
  private entityLabelFor(key: string, value: unknown): string | null {
    const id = `${value}`;

    switch (key) {
      case 'user': return this.listUsers.find(user => user._id === id)?.name ?? null;
      case 'product': return this.listProducts.find(product => product._id === id)?.name ?? null;
      case 'customer': return this.listCustomers.find(customer => customer._id === id)?.name ?? null;
      case 'lossReason': {
        const reason = this.listLossReasons.find(item => item._id === id);
        return reason ? this.lossReasonLabel(reason) : null;
      }
      case 'origin': return this.originOptions().find(origin => origin.value === id)?.label ?? null;
      default: return null;
    }
  }

  /**
   * A date-range filter, stated as the range itself: `dd/MM` on both ends — the year does
   * not fit a chip and is almost never the question.
   */
  private dateRangeSummary(key: string): string | null {
    const from = this.shortDate(this.searchParams?.[`${key}Start`]);
    if (!from) return null;

    const to = this.shortDate(this.searchParams?.[`${key}End`]);
    return to && to !== from ? `${from} – ${to}` : from;
  }

  /** A calendar day as the panel stores it, or the epoch milliseconds an earlier build persisted. */
  private shortDate(value: unknown): string | null {
    if (!this.isActiveValue(value)) return null;

    const date = this.toPickerDate(value);
    return date ? date.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' }) : null;
  }

  /**
   * The value a chip states, alongside its filter's name.
   *
   * A list spells its picks out, the last one joined by "ou": "Alta, Urgente ou Crítico"
   * reads as the OR it is, and the "e" between chips as the AND between filters. A date
   * range is keyed as `<key>Start` / `<key>End`, never under the definition's own key.
   */
  private buildDisplay(def: FilterDefinition, value: unknown): string {
    if (def.type === 'daterange') return this.dateRangeSummary(def.key) ?? '';

    if (Array.isArray(value)) {
      const labels = value.map(v => this.labelFor(def, v)).filter((label): label is string => !!label);
      // Nothing nameable yet — the four option lists load over chained requests. The
      // count is a thin answer; a row of raw ObjectIds is not an answer at all.
      if (!labels.length) return `${value.length}`;
      if (labels.length === 1) return labels[0];
      return `${labels.slice(0, -1).join(', ')} ${this.t('kanban.filterPanel.or')} ${labels[labels.length - 1]}`;
    }

    return this.labelFor(def, value) ?? '';
  }

  // ---------------------------------------------------------------------------------
  // Fitting the line: as many chips as the width allows, the rest behind "+N"
  // ---------------------------------------------------------------------------------

  /**
   * Chips fill the line in order until one no longer fits, up to `MAX_VISIBLE_CHIPS`; the
   * rest go behind the "+N" button, whose own room is reserved as soon as anything
   * overflows. Before the first measure (`availableWidth` 0) only the cap applies: the
   * line is never cut on a guess.
   */
  private fitChips(chips: Array<FilterChipView>, available: number): { visible: Array<FilterChipView>; overflow: Array<FilterChipView> } {
    if (available <= 0) return { visible: chips.slice(0, MAX_VISIBLE_CHIPS), overflow: chips.slice(MAX_VISIBLE_CHIPS) };

    let used = 0;
    let count = 0;
    for (const chip of chips) {
      if (count >= MAX_VISIBLE_CHIPS) break;
      const width = chip.width + (count > 0 ? CONNECTOR_WIDTH_PX : 0);
      if (used + width > available) break;
      used += width;
      count++;
    }

    if (count < chips.length) {
      while (count > 0 && used + MORE_BUTTON_WIDTH_PX > available) {
        used -= chips[count - 1].width + (count > 1 ? CONNECTOR_WIDTH_PX : 0);
        count--;
      }
    }

    return { visible: chips.slice(0, count), overflow: chips.slice(count) };
  }

  /**
   * A chip's rendered width, from its text in the bar's own font plus the chrome around
   * it, capped where the stylesheet truncates the value. Measured on a canvas rather
   * than on the DOM: the chips behind "+N" are not on the line, so they have no box to
   * measure, yet their width decides whether they would fit.
   */
  private measureChip(label: string, verb: string, value: string): number {
    const fonts = this.chipFontsOf();
    const text = this.textWidth(label, fonts.bold) + this.textWidth(verb, fonts.regular) + this.textWidth(value, fonts.regular);
    return Math.min(CHIP_MAX_WIDTH_PX, Math.ceil(CHIP_CHROME_PX + text));
  }

  private textWidth(text: string, font: string): number {
    if (!text) return 0;
    if (!this.measureContext && typeof document !== 'undefined') {
      this.measureContext = document.createElement('canvas').getContext('2d');
    }
    if (!this.measureContext) return text.length * 6.5;

    this.measureContext.font = font;
    return this.measureContext.measureText(text).width;
  }

  /** The chip area's computed font, read once it is on screen; the label is bold, the rest regular. */
  private chipFontsOf(): { regular: string; bold: string } {
    if (this.chipFonts) return this.chipFonts;

    const element = this.chipsArea?.nativeElement;
    if (!element || typeof getComputedStyle === 'undefined') return { regular: DEFAULT_CHIP_FONT, bold: `700 ${DEFAULT_CHIP_FONT}` };

    const style = getComputedStyle(element);
    const base = `${style.fontSize || '13px'} ${style.fontFamily || 'sans-serif'}`;
    this.chipFonts = { regular: `400 ${base}`, bold: `700 ${base}` };
    return this.chipFonts;
  }

  private observeChipArea(): void {
    const element = this.chipsArea?.nativeElement;
    if (!element || typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver(entries => {
      const width = Math.floor(entries[0]?.contentRect.width ?? 0);
      if (Math.abs(width - this.availableWidth()) < 1) return;
      // The observer fires outside Angular's zone; the signal write has to land inside it.
      this.ngZone.run(() => this.availableWidth.set(width));
    });
    this.resizeObserver.observe(element);
  }

  /** The widths are text measures: once the web font lands they change, so the chips are rebuilt. */
  private refreshChipsWhenFontsLoad(): void {
    if (typeof document === 'undefined' || !document.fonts?.ready) return;
    document.fonts.ready.then(() => this.ngZone.run(() => {
      this.chipFonts = null;
      this.refreshChips();
    }));
  }

  // ---------------------------------------------------------------------------------
  // Saved filters (SPEC-2026-10-crm-kanban-saved-filters)
  // ---------------------------------------------------------------------------------

  public isSavedActive(id: string): boolean {
    return this.activeSavedIds().has(id);
  }

  public openSave(): void {
    this.saveNameControl.setValue('');
    this.confirmDeleteId.set(null);
    this.saveOpen.set(true);
  }

  public cancelSave(): void {
    this.saveOpen.set(false);
  }

  public saveFilter(): void {
    const name = (this.saveNameControl.value ?? '').trim();
    if (!name || this.saving() || !this.selectedFunnel?._id) return;

    this.saving.set(true);
    this.cardSavedFilterService
      .save({ funnel: this.selectedFunnel._id, name, filters: this.activeFilters(this.searchParams), chips: this.activeChipOrder() })
      .pipe(takeUntil(this.destroy$), finalize(() => this.saving.set(false)))
      .subscribe({
        next: (envelope) => {
          this.savedFilters.update(list => [...list, envelope.data]);
          this.saveOpen.set(false);
          this.refreshActiveSaved();
        },
        // The field stays open with the name, so the user can retry (AC-22).
        error: (err) => this.alertService.error(err?.error?.message ?? this.t('kanban.filterPanel.savedFilterSaveError')),
      });
  }

  /**
   * A pill applies its saved filter (RF-12), and the active one takes it off the board (RF-28):
   * active means the applied set is exactly the saved one, so clearing it is deselecting it.
   */
  public toggleSavedFilter(saved: CardSavedFilter): void {
    if (this.isSavedActive(saved._id)) {
      this.clearFilters();
      return;
    }
    this.applySavedFilter(saved);
  }

  /** Replaces the applied set with a saved one and reloads the board; the free search stays (RF-12, RF-13). */
  private applySavedFilter(saved: CardSavedFilter): void {
    this.closeMenus();
    this.saveOpen.set(false);
    this.confirmDeleteId.set(null);
    this.applyFilterSet(saved.filters, saved.chips ?? []);
    this.refreshChips();
    this.dispatchQueryParams();
  }

  /** The applied set is left as it is, also when the deleted filter was the active one (RF-18). */
  public deleteSavedFilter(saved: CardSavedFilter): void {
    this.cardSavedFilterService
      .delete(saved._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.savedFilters.update(list => list.filter(item => item._id !== saved._id));
          this.confirmDeleteId.set(null);
          this.refreshActiveSaved();
        },
        error: (err) => this.alertService.error(err?.error?.message ?? this.t('kanban.filterPanel.savedFilterDeleteError')),
      });
  }

  /** The user's saved filters of this funnel (RF-02, RF-05); a reply for a funnel left meanwhile is dropped. */
  private loadSavedFilters(idFunnel: string): void {
    this.savedFilters.set([]);
    this.saveOpen.set(false);
    this.confirmDeleteId.set(null);
    this.refreshActiveSaved();

    this.cardSavedFilterService
      .getByFunnel(idFunnel)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (envelope) => {
          if (idFunnel !== this.appliedFunnelId) return;
          this.savedFilters.set(envelope?.data ?? []);
          this.savedLimit.set(envelope?.meta?.limit ?? null);
          this.refreshActiveSaved();
        },
        error: () => {
          if (idFunnel === this.appliedFunnelId) this.alertService.error(this.t('kanban.filterPanel.savedFiltersLoadError'));
        },
      });
  }

  private refreshActiveSaved(): void {
    const applied = this.filterSignature(this.searchParams);
    this.activeSavedIds.set(new Set(
      this.savedFilters().filter(saved => this.filterSignature(saved.filters) === applied).map(saved => saved._id),
    ));
  }

  /** The values in a comparable form: keys sorted, each list sorted — chip order and pick order do not count (RF-15). */
  private filterSignature(params: Record<string, unknown> | null | undefined): string {
    const entries = Object.entries(this.activeFilters(params))
      .map(([key, value]): [string, unknown] => [key, Array.isArray(value) ? value.map(String).sort() : value])
      .sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(entries);
  }

  /** The values that narrow the board, without the funnel: what a saved filter holds (plan AD-02). */
  private activeFilters(params: Record<string, unknown> | null | undefined): Record<string, unknown> {
    return Object.fromEntries(Object.entries(params ?? {}).filter(([key, value]) => key !== 'funnel' && this.isActiveValue(value)));
  }

  /** The order of the applied chips: what the remembered set and a saved filter keep. */
  private activeChipOrder(): Array<string> {
    return this.chipOrder.filter(key => this.isKeyActive(key));
  }

  // ---------------------------------------------------------------------------------
  // Showing and hiding the section
  // ---------------------------------------------------------------------------------

  public toggleSection(): void {
    this.sectionOpen.update(open => !open);
    this.writeSectionOpen(this.sectionOpen());
  }

  private readSectionOpen(): boolean {
    try {
      return localStorage.getItem(this.sectionOpenKey()) === '1';
    } catch {
      return false;
    }
  }

  private writeSectionOpen(open: boolean): void {
    try {
      localStorage.setItem(this.sectionOpenKey(), open ? '1' : '0');
    } catch {
      // A full or unavailable storage only stops the choice from being remembered.
    }
  }

  private sectionOpenKey(): string {
    return `${SECTION_OPEN_KEY_PREFIX}${this.getIDCurrentUser()}`;
  }

  // ---------------------------------------------------------------------------------
  // Plumbing
  // ---------------------------------------------------------------------------------

  /**
   * The filters, in the sections the board declares them in (`FilterDefinition.group`):
   * the split already exists upstream — `kanban.component.ts` builds `filterDefs` and
   * `radarFilterDefs` separately and stamps each batch on the way in. A definition with
   * no group falls into the first section rather than disappearing.
   *
   * Built into a signal on input change rather than computed from the template: the
   * menu would otherwise regroup every definition on every change-detection pass.
   */
  private buildFilterGroups(): void {
    const groups: Array<{ label: string; items: Array<FilterDefinition> }> = [];

    (this.definitions ?? []).forEach((def) => {
      const label = def.group ?? 'kanban.filterPanel.groupDeal';
      const group = groups.find(g => g.label === label);

      if (group) {
        group.items.push(def);
        return;
      }

      groups.push({ label, items: [def] });
    });

    this.filterGroups.set(groups);
  }

  private resetToDefaults(): void {
    const defaults = (this.definitions ?? []).reduce((acc, def) => {
      acc[def.key] = def.defaultValue;
      return acc;
    }, {} as Record<string, unknown>);

    this.searchParams = defaults;

    if (this.selectedFunnel?._id) {
      this.searchParams.funnel = this.selectedFunnel._id;
    }
  }

  private updateSearchParams(): void {
    const values = this.searchParams ?? {};
    const params = Object.entries(values).reduce((acc, [key, value]) => {
      if (this.isActiveValue(value)) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    if (this.selectedFunnel?._id) {
      params['funnel'] = this.selectedFunnel._id;
    }

    this.searchParams = params;
  }

  private isActiveValue(value: unknown): boolean {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return value?.length > 0;
    return true;
  }

  private setupGlobalSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((searchTerm) => {
        this.searchString = searchTerm || '';
        this.dispatchQueryParams();
      });
  }
}
